<template>
  <div>
    <transition name="fade" appear><div class="gk-modal-backdrop" v-if="showModal" @click="close"></div></transition>
    <transition name="pop" appear>
      <div class="gk-modal-wrapper" v-if="showModal" :class="[wrapperClass, { 'modal-on-modal': modalOnModal }]">
        <div class="gk-modal" :class="modalClass" :style="computedStyles">
          <slot name="background" />
          <div class="gk-modal-content">
            <slot name="titlebar" />
            <div class="modal-content-wrap" :class="{ 'has-actions': hasActions }">
              <LoadingSpinner v-if="loading" />
              <overlay-scrollbars v-if="overlayScrollb" :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' } }">
                <slot name="maincontent" />
              </overlay-scrollbars>
              <slot v-else name="maincontent" />
            </div>
            <div class="action-buttons-wrapper" :class="{ 'has-actions': hasActions }">
              <slot name="actions" />
            </div>
            <slot name="premiumtip" />
            <GekkoButton 
              v-if="closeable"
              @click="close()"
              icon="fa-solid fa-xmark"
              variant="link"
              size="lg"
              class="close-btn text-unassigned"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex';

import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import GekkoButton from "@/components/GekkoButton"
export default {
  name: "ModalBase",
  components: {
    LoadingSpinner, GekkoButton
  },
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    autoH: {
      type: Boolean,
      default: false
    },
    fullH: {
      type: Boolean,
      default: false
    },
    fullAutoH: {
      type: Boolean,
      default: false
    },
    narrow: {
      type: Boolean,
      default: false
    },
    widthMs: {
      type: Boolean,
      default: false
    },
    widthMd: {
      type: Boolean,
      default: false
    },
    widthXl: {
      type: Boolean,
      default: false
    },
    extraNarrow: {
      type: Boolean,
      default: false
    },
    short: {
      type: Boolean,
      default: false
    },
    overlayScrollb: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    onTop: {
      type: Boolean,
      default: false
    },
    closeable: {
      type: Boolean,
      default: true
    },
    escToClose: {
      type: Boolean,
      default: true
    },
    fullScreen: {
      type: Boolean,
      default: false
    },
    mobileFullScreen: {
      type: Boolean,
      default: false
    },
    injectStore: {
      type: Object,
      default: null
    },
    unpadded: {
      type: Boolean,
      default: false
    },
    dimensions: {
      type: Object,
      default: null
    },
    modalModal: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
    }
  },
  computed: {
    ...mapGetters({ 
      masterModal: "modals/getMasterModal",
    }),
    hasActions() {
      return !!this.$slots['actions']
    },
    wrapperClass () {
      return [
        (this.modalModal) ? 'modal-on-modal' : '',
        (this.onTop) ? 'gk-modal-wrapper-top' : '',
        (this.fullScreen) ? 'gk-modal-fullscreen' : '',
        (this.mobileFullScreen) ? 'gk-modal-mobile-fullscreen' : '',
      ]
    },
    modalClass () {
      return [
        (this.short) ? 'gk-modal-h-sm' : '',
        (this.autoH) ? 'gk-modal-h-auto' : '',
        (this.fullH) ? 'gk-modal-h-full' : '',
        (this.fullAutoH) ? 'gk-modal-h-fullauto' : '',
        (this.narrow) ? 'gk-modal-w-sm' : '',
        (this.widthMs) ? 'gk-modal-w-ms' : '',
        (this.widthMd) ? 'gk-modal-w-md' : '',
        (this.widthXl) ? 'gk-modal-w-xl' : '',
        (this.extraNarrow) ? 'gk-modal-w-xs' : '',
        (this.unpadded) ? 'gk-modal-unpadded' : ''
      ]
    },
    modalOnModal () {
      return (this.$store && this.masterModal) || this.modalModal
    },
    computedStyles () {
      let styles = ''
      if (this.dimensions) {
        styles = 'max-width: ' + this.dimensions.width + 'px; max-height: ' + this.dimensions.height + 'px;'
      }
      return styles
      // return 'width: 200px; height: 200px;'
    }
  },
  watch: {
    showModal (newValue, oldValue) {
      if (newValue) {
        this.setStoreModal(this.$parent.$options.name)
      } else {
        this.setStoreModal(false)
      }
    }
  },
  created () {
    if (this.injectStore) {
      this.$store = this.injectStore
    }
    window.addEventListener('keydown', this.closeEventListener)
    if (this.showModal) {
      this.setStoreModal(this.$parent.$options.name)
    }
  },
  destroyed () {
    window.removeEventListener('keydown', this.closeEventListener)
    this.setStoreModal(false)
  },
  methods: {
    ...mapMutations({ 
      setModal: 'modals/SET_MODAL'
    }),
    closeEventListener (event) {
      if (this.escToClose && this.showModal && event.key === 'Escape') this.close()
    },
    close () {
      if (this.closeable) {
        document.body.classList.remove('modal-on')
        this.$emit('close-modal')
      }
    },
    setStoreModal (modal) {
      if (this.$store) {
        this.setModal(modal)
      }
    }
  }
};
</script>

<style lang="scss">
  .gk-modal-backdrop {
    height: auto;
    background: rgb(0,0,0,0.3);
    position: fixed;
    top: 0;
    left: 0;
    width: auto;
    z-index: 1005;
    right: 0;
    bottom: 0;
  }
  .gk-modal-wrapper {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    padding: 0.5rem;
    z-index: 1010;
    display: flex;
    overflow-y: auto;
    overflow-x: hidden;
    &.gk-modal-wrapper-top {
      .gk-modal {
        margin-top: 0;
      }
    }
    @media (min-width: 576px) {
      padding: 1rem;
      &.modal-on-modal {
        padding: 2.5rem;
      }
    }
    .gk-modal {
      background: white;
      margin: auto;
      width: 100%;
      height: 100%;
      // overflow: hidden;
      transform: none;
      flex: 1 1 auto;
      padding: 0.5rem;
      box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1),0 8px 10px -6px rgb(0 0 0 / 0.1);
      @media (min-width: 769px) {
        padding: 1rem;
      }
      border-radius: $radiusLg;
      max-width: 1200px;

      position: relative;
      .gk-modal-content {
        position: relative;
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
        .close-btn {
          position: absolute;
          cursor: pointer;
          z-index: 9001;
          padding: 0.25rem 1rem;

          top: -0.5rem;
          right: -0.5rem;
          @media (min-width: 769px) {
            top: -1rem;
            right: -1rem;
          }
        } 

        .close-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          color: white !important;
          background: black;
          width: 28px;
          height: 28px;
          padding: 0;
          border: 0px;
          &:hover { background: #e03131; }

          top: calc(-10px - 0.25rem);
          right: calc(-10px - 0.25rem);
          @media (min-width: 769px) {
            top: calc(-10px - 1rem);
            right: calc(-10px - 1rem);
          }
        }

        .modal-content-wrap {
          flex: 1;
          overflow: visible;
          height: calc(100% - 70px);
          .os-host {
            height: 100%;
          }
          &.has-actions {
            height: calc(100% - 120px);
          }
          .os-host-overflow-y {
            padding-right: 1rem;
          }
        }
      }
      .action-buttons-wrapper {
        z-index: 1;
        &.has-actions {
          padding-top: 1rem;
        }
      }

      .modal-dropzone-actions {
        display: flex;
        flex-direction: column;
        padding-bottom: 1rem;
        &--wrap {
          height: 100%;
          padding: 10px;
          border-radius: $radiusMd;
          background: $inputBackgroundColor;
          box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
          display: block;
        }
        .modal-dropzone-wrapper {
          flex: 1;
          height: 100%;
        }
        .modal-main-actions {
          height: auto;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          .hint {
            font-size: 14px;
            padding-left: 10px;
            margin-bottom: -3px;
            line-height: 1.2;
          }
        }
        @media (min-width: 576px) {
          padding-bottom: 0px;
        }
      }


    }
  }

  .modal-on, .master-modal-on {
    overflow: hidden;
  }

  .gk-modal {
    transition: all 0.3s ease;
    &.gk-modal-h-auto {
      height: auto !important;
      overflow: visible !important;
    }
    &.gk-modal-h-full {
      display: flex;
      height: auto;
      min-height: 100%;
      .gk-modal-content {
        height: auto;
        flex: 1;
      }
    }
    &.gk-modal-h-fullauto {
      height: auto !important;
      overflow: visible !important;
      max-height: auto !important;
    }

    .gk-modal-label {
      margin-bottom: 0;
      font-size: 14px;
      color: $colorTextBase;
    }
    .modal-form-legend {
      margin-top: 20px;
      legend {
        font-size: 16px;
        font-family: DINNextLTPro-Medium, sans-serif;
      }
    }
    .modal-form-legend:first-child {
      margin-top: 0px;
    }
    
  }
  // .gk-modal-fullscreen {
  //   @media (max-width: 2000px) {
  //     padding: 0;
  //   }
  //   .gk-modal {
  //     transition: all 0.3s ease;
  //     @media (max-width: 2000px) {
  //       max-width: 100% !important;
  //       max-height: 100% !important;
  //       width: 100%;
  //       height: 100%;
  //       border-radius: 0;
  //       .titlebar.title-modal {
  //         border-radius: 0;
  //       }
  //     }
  //   }
  // }
  .gk-modal-mobile-fullscreen {
    padding: 0;
    .gk-modal {
      width: 100% !important;
      max-width: 100% !important;
      height: auto !important;
      max-height: initial !important;
      min-height: 100% !important;
      border-radius: 0;
      .titlebar.title-modal {
        border-radius: 0;
      }
    }
  }

  .gk-modal-unpadded {
    .gk-modal-content {
      .titlebar.title-modal {
        padding: 0.5rem !important;
        @media (min-width: '768px') {
          width: 100% !important;
          margin: 0 !important;
          padding: 1rem !important;
        }
      }
    }
  }
</style>

<style lang="scss" scoped>
  .action-button {
    font-size: 14px;
  }
  .gk-modal-w-xl {
    max-width: 1600px;
  }
  .gk-modal-w-md {
    max-width: 900px !important;
  }
  .gk-modal-w-ms {
    max-width: 750px !important;
  }
  .gk-modal-w-sm {
    max-width: 600px !important;
  }
  .gk-modal-w-xs {
    max-width: 420px !important;
  }
  .gk-modal-h-sm {
    max-height: 500px !important;
  }
  .gk-modal-h-fullauto {
    max-height: 100% !important;
  }
  .gk-modal-unpadded {
    padding: 0 !important;
    .gk-modal-content {
      .modal-content-wrap {
        height: calc(100% - 83px);
      }
    }
    // .close-btn {
    //   display: flex;
    //   align-items: center;
    //   justify-content: center;
    //   top: 1rem !important;
    //   right: 1rem !important;
    //   padding: 0 !important;
    //   height: 24px !important;
    //   width: 24px !important;
    //   margin: 0;
    // }
    .close-btn {
      top: -10px !important;
      right: -10px !important;
    }
  }
</style>
